import useUrl from './helpers/useUrl'

/**
 * @param {import('@nuxtjs/axios').NuxtAxiosInstance} client
 * */
export default (client) => {
  const root = useUrl('levels')

  return {
    /**
     * @returns {Promise<AxiosResponse<GetListAllResponse>>}
    * */
    getListAll() {
      const url = root('list/all')
      return client.get(url)
    },
    /**
     * @param {Object} GetUserLevelDTO
     * @param {boolean} [GetUserLevelDTO.joinGifts]
     * @param {boolean} [GetUserLevelDTO.joinNextLevel]
     * @param {boolean} [GetUserLevelDTO.joinRarity]
     *
     * @returns {Promise<AxiosResponse<GetUserLevelResponse>>}
     * */
    getUserLevel({ joinGifts, joinNextLevel, joinRarity }) {
      const url = root('account')
      const params = {
        joinGifts,
        joinNextLevel,
        joinRarity,
      }
      return client.get(url, { params })
    },

    /**
     * @returns {Promise<AxiosResponse<GetLeaderboardResponse>>}
     * */
    getLeaderboard() {
      const url = root('leaderboard')
      return client.get(url)
    },

    /**
     * @returns {Promise<AxiosResponse<GetLeaderboardResponse>>}
     * */
    getLeaderboardLevel() {
      const url = root('leaderboard/level')
      return client.get(url)
    },

    /**
     * @returns {Promise<AxiosResponse<GetLevelStatisticResponse>>}
     * */
    getLevelStatistic() {
      const url = root('account/statistic')
      return client.get(url)
    },

    /**
     * @returns {Promise<AxiosResponse<GetEventStaticPrizeResponse>>}
     * */
    getEventStaticPrize() {
      const url = root('events-static-prize')
      return client.get(url)
    },
  }
}

/** @typedef {object} GetListAllResponse
 * @property {boolean} status
 * @property {object[]} result
 * @property {number} result.id
 * @property {string} result.createdAt
 * @property {string} result.updatedAt
 * @property {number} result.level
 * @property {string} result.name
 * @property {number} result.expPointsToNextLevel
 * @property {string} result.iconUrl
 * @property {object} result.gifts
 * @property {|object[]} result.gifts.oneTime
 * @property {|object[]} result.gifts.daily
 * @property {|object[]} result.gifts.weekly
 * @property {|object[]} result.gifts.insurance
 * @property {number} result.gifts.oneTime.id
 * @property {string} result.gifts.oneTime.createdAt
 * @property {string} result.gifts.oneTime.updatedAt
 * @property {string} result.gifts.oneTime.name
 * @property {string} result.gifts.oneTime.shortName
 * @property {string} result.gifts.oneTime.uuid
 * @property {string} result.gifts.oneTime.description
 * @property {string} result.gifts.oneTime.iconUrl
 * @property {string} result.gifts.oneTime.serviceName
 * @property {number} result.gifts.oneTime.serviceReferenceId
 * @property {string} result.gifts.oneTime.type
 * @property {number} result.gifts.insurance.id
 * @property {string} result.gifts.insurance.createdAt
 * @property {string} result.gifts.insurance.updatedAt
 * @property {string} result.gifts.insurance.name
 * @property {string} result.gifts.insurance.shortName
 * @property {string} result.gifts.insurance.uuid
 * @property {string} result.gifts.insurance.description
 * @property {string} result.gifts.insurance.iconUrl
 * @property {string} result.gifts.insurance.serviceName
 * @property {number} result.gifts.insurance.serviceReferenceId
 * @property {string} result.gifts.insurance.type
 * @property {number} result.gifts.weekly.id
 * @property {string} result.gifts.weekly.createdAt
 * @property {string} result.gifts.weekly.updatedAt
 * @property {string} result.gifts.weekly.name
 * @property {string} result.gifts.weekly.shortName
 * @property {string} result.gifts.weekly.uuid
 * @property {string} result.gifts.weekly.description
 * @property {string} result.gifts.weekly.iconUrl
 * @property {string} result.gifts.weekly.serviceName
 * @property {number} result.gifts.weekly.serviceReferenceId
 * @property {string} result.gifts.weekly.type
 * @property {number} result.gifts.daily.id
 * @property {string} result.gifts.daily.createdAt
 * @property {string} result.gifts.daily.updatedAt
 * @property {string} result.gifts.daily.name
 * @property {string} result.gifts.daily.shortName
 * @property {string} result.gifts.daily.uuid
 * @property {string} result.gifts.daily.description
 * @property {string} result.gifts.daily.iconUrl
 * @property {string} result.gifts.daily.serviceName
 * @property {number} result.gifts.daily.serviceReferenceId
 * @property {string} result.gifts.daily.type
 */

/** @typedef {object} GetUserLevelResponse
 * @property {boolean} status
 * @property {object} result
 * @property {number} result.userId
 * @property {number} result.accumulatedExpPoints
 * @property {object} result.currentLevel
 * @property {number} result.currentLevel.id
 * @property {string} result.currentLevel.createdAt
 * @property {string} result.currentLevel.updatedAt
 * @property {number} result.currentLevel.level
 * @property {string} result.currentLevel.name
 * @property {number} result.currentLevel.expPointsToNextLevel
 * @property {string} result.currentLevel.iconUrl
 * @property {object} result.currentLevel.gifts
 * @property {object[]} result.currentLevel.gifts.oneTime
 * @property {number} result.currentLevel.gifts.oneTime.id
 * @property {string} result.currentLevel.gifts.oneTime.createdAt
 * @property {string} result.currentLevel.gifts.oneTime.updatedAt
 * @property {string} result.currentLevel.gifts.oneTime.name
 * @property {string} result.currentLevel.gifts.oneTime.shortName
 * @property {string} result.currentLevel.gifts.oneTime.type
 * @property {string} result.currentLevel.gifts.oneTime.uuid
 * @property {string} result.currentLevel.gifts.oneTime.description
 * @property {string} result.currentLevel.gifts.oneTime.iconUrl
 * @property {string} result.currentLevel.gifts.oneTime.serviceName
 * @property {number} result.currentLevel.gifts.oneTime.serviceReferenceId
 * @property {object[]} result.currentLevel.gifts.daily
 * @property {number} result.currentLevel.gifts.daily.id
 * @property {string} result.currentLevel.gifts.daily.createdAt
 * @property {string} result.currentLevel.gifts.daily.updatedAt
 * @property {string} result.currentLevel.gifts.daily.name
 * @property {string} result.currentLevel.gifts.daily.shortName
 * @property {string} result.currentLevel.gifts.daily.type
 * @property {string} result.currentLevel.gifts.daily.uuid
 * @property {string} result.currentLevel.gifts.daily.description
 * @property {string} result.currentLevel.gifts.daily.iconUrl
 * @property {string} result.currentLevel.gifts.daily.serviceName
 * @property {number} result.currentLevel.gifts.daily.serviceReferenceId
 * @property {object[]} result.currentLevel.gifts.weekly
 * @property {number} result.currentLevel.gifts.weekly.id
 * @property {string} result.currentLevel.gifts.weekly.createdAt
 * @property {string} result.currentLevel.gifts.weekly.updatedAt
 * @property {string} result.currentLevel.gifts.weekly.name
 * @property {string} result.currentLevel.gifts.weekly.shortName
 * @property {string} result.currentLevel.gifts.weekly.type
 * @property {string} result.currentLevel.gifts.weekly.uuid
 * @property {string} result.currentLevel.gifts.weekly.description
 * @property {string} result.currentLevel.gifts.weekly.iconUrl
 * @property {string} result.currentLevel.gifts.weekly.serviceName
 * @property {number} result.currentLevel.gifts.weekly.serviceReferenceId
 * @property {object[]} result.currentLevel.gifts.insurance
 * @property {number} result.currentLevel.gifts.insurance.id
 * @property {string} result.currentLevel.gifts.insurance.createdAt
 * @property {string} result.currentLevel.gifts.insurance.updatedAt
 * @property {string} result.currentLevel.gifts.insurance.name
 * @property {string} result.currentLevel.gifts.insurance.shortName
 * @property {string} result.currentLevel.gifts.insurance.type
 * @property {string} result.currentLevel.gifts.insurance.uuid
 * @property {string} result.currentLevel.gifts.insurance.description
 * @property {string} result.currentLevel.gifts.insurance.iconUrl
 * @property {string} result.currentLevel.gifts.insurance.serviceName
 * @property {number} result.currentLevel.gifts.insurance.serviceReferenceId
 * @property {object} result.nextLevel
 * @property {number} result.nextLevel.id
 * @property {string} result.nextLevel.createdAt
 * @property {string} result.nextLevel.updatedAt
 * @property {number} result.nextLevel.level
 * @property {string} result.nextLevel.name
 * @property {number} result.nextLevel.expPointsToNextLevel
 * @property {string} result.nextLevel.iconUrl
 * @property {object} result.nextLevel.gifts
 * @property {object[]} result.nextLevel.gifts.oneTime
 * @property {number} result.nextLevel.gifts.oneTime.id
 * @property {string} result.nextLevel.gifts.oneTime.createdAt
 * @property {string} result.nextLevel.gifts.oneTime.updatedAt
 * @property {string} result.nextLevel.gifts.oneTime.name
 * @property {string} result.nextLevel.gifts.oneTime.shortName
 * @property {string} result.nextLevel.gifts.oneTime.type
 * @property {string} result.nextLevel.gifts.oneTime.uuid
 * @property {string} result.nextLevel.gifts.oneTime.description
 * @property {string} result.nextLevel.gifts.oneTime.iconUrl
 * @property {string} result.nextLevel.gifts.oneTime.serviceName
 * @property {number} result.nextLevel.gifts.oneTime.serviceReferenceId
 * @property {object[]} result.nextLevel.gifts.daily
 * @property {number} result.nextLevel.gifts.daily.id
 * @property {string} result.nextLevel.gifts.daily.createdAt
 * @property {string} result.nextLevel.gifts.daily.updatedAt
 * @property {string} result.nextLevel.gifts.daily.name
 * @property {string} result.nextLevel.gifts.daily.shortName
 * @property {string} result.nextLevel.gifts.daily.type
 * @property {string} result.nextLevel.gifts.daily.uuid
 * @property {string} result.nextLevel.gifts.daily.description
 * @property {string} result.nextLevel.gifts.daily.iconUrl
 * @property {string} result.nextLevel.gifts.daily.serviceName
 * @property {number} result.nextLevel.gifts.daily.serviceReferenceId
 * @property {object[]} result.nextLevel.gifts.weekly
 * @property {number} result.nextLevel.gifts.weekly.id
 * @property {string} result.nextLevel.gifts.weekly.createdAt
 * @property {string} result.nextLevel.gifts.weekly.updatedAt
 * @property {string} result.nextLevel.gifts.weekly.name
 * @property {string} result.nextLevel.gifts.weekly.shortName
 * @property {string} result.nextLevel.gifts.weekly.type
 * @property {string} result.nextLevel.gifts.weekly.uuid
 * @property {string} result.nextLevel.gifts.weekly.description
 * @property {string} result.nextLevel.gifts.weekly.iconUrl
 * @property {string} result.nextLevel.gifts.weekly.serviceName
 * @property {number} result.nextLevel.gifts.weekly.serviceReferenceId
 * @property {object[]} result.nextLevel.gifts.insurance
 * @property {number} result.nextLevel.gifts.insurance.id
 * @property {string} result.nextLevel.gifts.insurance.createdAt
 * @property {string} result.nextLevel.gifts.insurance.updatedAt
 * @property {string} result.nextLevel.gifts.insurance.name
 * @property {string} result.nextLevel.gifts.insurance.shortName
 * @property {string} result.nextLevel.gifts.insurance.type
 * @property {string} result.nextLevel.gifts.insurance.uuid
 * @property {string} result.nextLevel.gifts.insurance.description
 * @property {string} result.nextLevel.gifts.insurance.iconUrl
 * @property {string} result.nextLevel.gifts.insurance.serviceName
 * @property {number} result.nextLevel.gifts.insurance.serviceReferenceId
 * @property {boolean} result.isShowPopup
 * @property {boolean} result.isTutorialEnded
 */

/** @typedef {object} GetLeaderboardResponse
 * @property {boolean} status
 * @property {object} result
 * @property {object[]} result.leaderboard
 * @property {number} result.leaderboard.userId
 * @property {string} result.leaderboard.iconUrl
 * @property {string} result.leaderboard.name
 * @property {number} result.leaderboard.accumulatedExpPoints
 * @property {boolean} result.leaderboard.isUserFromRequest
 * @property {object} result.accountInfo
 * @property {number} result.accountInfo.expPointToTop
 */

/** @typedef {object} GetLevelStatisticResponse
 * @property {boolean} status
 * @property {object} result
 * @property {object} result.accumulatedExpBetReal
 * @property {number} result.accumulatedExpBetReal.received
 * @property {object} result.accumulatedExpBetBonus
 * @property {number} result.accumulatedExpBetBonus.received
 * @property {object} result.accumulatedExpBm
 * @property {number} result.accumulatedExpBm.received
 * @property {object} result.accumulatedExpWof
 * @property {number} result.accumulatedExpWof.received
 * @property {object} result.accumulatedExpRaffle
 * @property {number} result.accumulatedExpRaffle.received
 * @property {object} result.accumulatedExpTournament
 * @property {number} result.accumulatedExpTournament.received
 * @property {object} result.accumulatedExpFirstDep
 * @property {number} result.accumulatedExpFirstDep.total
 * @property {number} result.accumulatedExpFirstDep.received
 * @property {object} result.accumulatedExpVerification
 * @property {number} result.accumulatedExpVerification.total
 * @property {number} result.accumulatedExpVerification.received
 */

/** @typedef {object} GetEventStaticPrizeResponse
 * @property {boolean} status
 * @property {object} result
 * @property {number} result.emailConfirmed
 * @property {number} result.phoneConfirmed
 * @property {number} result.documentConfirmed
 * @property {number} result.firstDeposit
 * @property {number} result.wofSpin
 * @property {number} result.bmOpen
 * @property {number} result.tournamentWinner
 * @property {number} result.raffleWinner
 * @property {number} result.subraffleWinner
 */
